import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type FeatureFlagName,
  type FeatureFlags,
  defaultFeatureFlags,
  featureFlagNames,
} from '@ipfabric/utils/featureFlag';
import type { FC, ReactNode } from 'react';

let featureFlags: FeatureFlags | null = null;
const loadLocalStorageFeatureFlags = (): Partial<FeatureFlags> => {
  const flagsStr = localStorage.getItem('featureFlags');
  if (!flagsStr) {
    return {};
  }

  const flags = flagsStr.split(',');
  return flags.reduce<Partial<FeatureFlags>>((acc, flag) => {
    let flagName: FeatureFlagName;
    let value: boolean;
    if (flag[0] === '!') {
      flagName = flag.slice(1) as FeatureFlagName;
      value = false;
    } else {
      flagName = flag as FeatureFlagName;
      value = true;
    }
    if (!featureFlagNames.includes(flagName)) {
      stumble(
        `Unknown feature flag in local storage featureFlags: ${flagName}`,
      );
    }
    acc[flagName] = value;
    return acc;
  }, {});
};
/**
 * Flags should be loaded only once upon page load, then not altered again.
 *
 * The function consumes data from api endpoint that returns only enabled flags,
 * omitting disable ones to reduce users' hacking temptation.
 *
 * @internal
 */
export const loadFeatureFlags = (enabledFlags: Partial<FeatureFlags>): void => {
  featureFlags = {
    ...defaultFeatureFlags,
    ...enabledFlags,
    ...loadLocalStorageFeatureFlags(),
  };
};
/**
 * Load a fixed set of feature flags for automated testing purposes.
 */
export const loadTestFeatureFlags = (
  enabledFlags?: Partial<FeatureFlags>,
): void => {
  featureFlags = {
    ...defaultFeatureFlags,
    ...enabledFlags,
  };
};
/**
 * Determine whether a particular feature flag is enabled in backend configuration.
 *
 * Regarding what feature flags are used for, we store them globally, accessible
 * via this getter function, to be simply usable anywhere in code.
 *
 * No need to propagate them from some state or so, making later cleanup easy.
 *
 * Just keep in mind that they can be used only after initial app data are loaded.
 */
export const isFeatureFlagEnabled = (flag: FeatureFlagName): boolean => {
  if (featureFlags === null) {
    stumble(
      `Trying to access feature flag ${flag} while feature flags were not loaded yet.`,
    );
    return defaultFeatureFlags[flag];
  }
  return featureFlags[flag];
};
type IfFeatureFlagProps = {
  flag: FeatureFlagName;
  disabled: ReactNode;
  enabled: ReactNode;
};
export const IfFeatureFlag: FC<IfFeatureFlagProps> = (props) => {
  if (isFeatureFlagEnabled(props.flag)) {
    return props.enabled;
  }

  return props.disabled;
};
